import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TestimonialProps {
  quote: string;
  author: string;
  role: string;
  school: string;
  image: string;
  alt: string;
}

const TestimonialCard = ({ quote, author, role, school, image, alt }: TestimonialProps) => {
  return (
    <div className="bg-card rounded-xl p-6 border border-border shadow-brand-sm hover:shadow-brand-md transition-all duration-300">
      <div className="flex items-start gap-4 mb-4">
        <div className="relative w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
          <AppImage
            src={image}
            alt={alt}
            fill
            className="object-cover" />

        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-text-primary">{author}</h4>
          <p className="text-sm text-text-secondary">{role}</p>
          <p className="text-sm text-brand-primary">{school}</p>
        </div>
      </div>
      <div className="flex gap-1 mb-4">
        {[...Array(5)].map((_, i) =>
        <Icon key={i} name="StarIcon" size={16} variant="solid" className="text-accent" />
        )}
      </div>
      <p className="text-text-secondary leading-relaxed italic">&ldquo;{quote}&rdquo;</p>
    </div>);

};

interface StudentOutcomesProps {
  className?: string;
}

const StudentOutcomes = ({ className = '' }: StudentOutcomesProps) => {
  const testimonials: TestimonialProps[] = [
  {
    quote: 'This program completely changed how I think about science. Building our hydroponic system and seeing the plants grow from our data analysis made everything click. Now I want to study agricultural engineering in college.',
    author: 'Maria Rodriguez',
    role: '8th Grade Student',
    school: 'Lincoln Middle School',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec52d7ac-1763300230694.png",
    alt: 'Smiling Hispanic teenage girl with long dark hair wearing blue school uniform'
  },
  {
    quote: 'My students are more engaged than ever before. The hands-on nature of Farm of Thought brings abstract concepts to life. We have seen dramatic improvements in test scores and student confidence in STEM subjects.',
    author: 'James Chen',
    role: 'Science Teacher',
    school: 'Roosevelt High School',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10867eebb-1763300721328.png",
    alt: 'Asian male teacher in his thirties wearing glasses and gray sweater smiling in classroom'
  },
  {
    quote: 'The technology integration is incredible. Programming the sensors and building the monitoring dashboard taught me coding skills I use every day. This program opened doors I did not know existed.',
    author: 'DeShawn Williams',
    role: '7th Grade Student',
    school: 'Washington STEM Academy',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1476e5b5b-1763296926502.png",
    alt: 'Young Black male student with short hair wearing red hoodie smiling confidently'
  }];


  const outcomes = [
  {
    icon: 'CodeBracketIcon',
    stat: '80%',
    label: 'of students became proficient in basic coding languages'
  },
  {
    icon: 'DocumentCheckIcon',
    stat: '100%',
    label: 'authored at least one functional program'
  },
  {
    icon: 'UserGroupIcon',
    stat: '60+',
    label: 'families impacted'
  },
  {
    icon: 'ScaleIcon',
    stat: '600+',
    label: 'pounds of food distributed'
  }];


  return (
    <section className={`py-20 bg-surface ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
            Measurable Student Outcomes
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Data-driven results showcasing the transformative impact on student learning and development
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {outcomes.map((outcome, index) =>
          <div
            key={index}
            className="bg-card rounded-xl p-6 border border-border text-center hover:shadow-brand-md transition-all duration-300">

              <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name={outcome.icon as any} size={24} className="text-brand-primary" />
              </div>
              <div className="text-4xl font-bold text-brand-primary mb-3">{outcome.stat}</div>
              <p className="text-sm text-text-secondary">{outcome.label}</p>
            </div>
          )}
        </div>

        <div className="bg-[#4F744A] rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Bring Farm for Thought to Your School?
          </h3>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Join schools transforming STEM education through hands-on hydroponics and climate tech innovation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://forms.monday.com/forms/55bca5b23ea1d6934bd7329ca28ccfc4?r=use1"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-[#4F744A] font-semibold rounded-lg hover:bg-gray-100 hover:shadow-brand-md hover:-translate-y-1 transition-all duration-300">

              Request Program Information
            </a>
          </div>
        </div>

        <div className="bg-surface rounded-2xl p-8 border border-border mt-12">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0 w-16 h-16 bg-brand-primary rounded-xl flex items-center justify-center">
              <Icon name="AcademicCapIcon" size={32} className="text-brand-primary-foreground" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-bold text-text-primary mb-2">Interested in a program? 

              </h3>
              <p className="text-text-secondary">Speak directly with our team to learn about program details, pricing, onboarding support and ongoing technical assistance.

              </p>
            </div>
            <a
              href="https://calendar.notion.so/meet/eehudson/foodforthought"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-brand-primary text-brand-primary-foreground font-semibold rounded-lg hover:bg-brand-primary/90 hover:shadow-brand-md hover:-translate-y-1 transition-all duration-300 whitespace-nowrap inline-block text-center">Schedule a Call


            </a>
          </div>
        </div>
      </div>
    </section>);

};

export default StudentOutcomes;